/**
 * OCR Extract Card — extraction IA typée pour docs légaux étendus.
 * Types : statuts · KBIS · bilan · PV AGO · PV AGE · cession de parts · dépôt greffe · autre.
 */
import { useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Upload, Loader2, CheckCircle2, FileText, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  { key: "statuts", label: "Statuts", hint: "Profil société + associés" },
  { key: "kbis", label: "KBIS", hint: "Profil société + dirigeants" },
  { key: "bilan", label: "Bilan / Liasse fiscale", hint: "Pré-remplit AGO" },
  { key: "cession_parts", label: "Acte de cession de parts", hint: "Pré-remplit AGE" },
  { key: "pv_ago", label: "PV AGO", hint: "Historique décisions" },
  { key: "pv_age", label: "PV AGE", hint: "Historique décisions" },
  { key: "depot_greffe", label: "Récépissé dépôt greffe", hint: "Verrouille échéance" },
  { key: "convention_reglementee", label: "Convention réglementée", hint: "→ registre auto" },
  { key: "feuille_presence", label: "Feuille de présence AG", hint: "→ registre auto" },
  { key: "autre", label: "Autre document", hint: "Extraction générique" },
];

const formatVal = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString("fr-FR");
  if (typeof v === "boolean") return v ? "Oui" : "Non";
  if (Array.isArray(v)) return `${v.length} élément(s)`;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60) + "…";
  return String(v);
};

export default function OcrExtractCard({ onAgoPrefilled, onAgePrefilled }) {
  const [docType, setDocType] = useState("bilan");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  const extract = async (file) => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Route vers l'endpoint le plus utile selon le type
      let url = `/legal/ocr/extract-typed?doc_type=${docType}`;
      if (docType === "bilan") url = "/legal/ocr/prefill-ago-from-bilan?persist=true";
      else if (docType === "cession_parts") url = "/legal/ocr/prefill-age-from-cession";
      else if (docType === "depot_greffe") url = "/legal/ocr/confirm-depot-greffe";
      const r = await api.post(url, fd);

      // Auto-push vers le registre pour convention_reglementee & feuille_presence
      // + persistance PDF dans le coffre-fort avec lien inversé
      let autoRegisterCreated = false;
      let linkedDocumentId = null;
      if ((docType === "convention_reglementee" || docType === "feuille_presence") && r.data?.extracted) {
        try {
          // Upload PDF au coffre-fort
          const catMap = {
            convention_reglementee: "convention",
            feuille_presence: "pv_assemblee",
          };
          const upFd = new FormData();
          upFd.append("file", file);
          const upR = await api.post(
            `/legal/documents/upload?category=${catMap[docType]}&tags=auto-ocr`,
            upFd
          );
          linkedDocumentId = upR?.data?.id || null;
          // iter64 : PATCH le doc avec un snippet indexable pour le full-text search
          if (linkedDocumentId) {
            try {
              const ex = r.data.extracted;
              const snippetParts = [];
              for (const [k, v] of Object.entries(ex || {})) {
                if (v === null || v === undefined || typeof v === "boolean") continue;
                if (Array.isArray(v)) {
                  snippetParts.push(v.map(x => typeof x === "object" ? JSON.stringify(x) : String(x)).join(" "));
                } else if (typeof v === "object") {
                  snippetParts.push(JSON.stringify(v));
                } else {
                  snippetParts.push(`${k}: ${v}`);
                }
              }
              await api.patch(`/legal/documents/${linkedDocumentId}`, {
                extracted_snippet: snippetParts.join(" · ").slice(0, 4000),
              });
            } catch { /* silent — snippet best-effort */ }
          }
        } catch { /* upload optionnel — on continue sans */ }

        try {
          const ex = r.data.extracted;
          const entry = docType === "convention_reglementee"
            ? {
                type: "conventions_reglementees",
                date_operation: ex.date_convention || ex.date_effet || new Date().toISOString().slice(0, 10),
                titre: `Convention — ${ex.objet || ex.beneficiaire_nom || "sans objet"}`.slice(0, 120),
                description: `Type: ${ex.type_convention || "?"} · Bénéficiaire: ${ex.beneficiaire_nom || "?"} (${ex.beneficiaire_fonction || "?"}) · Montant: ${ex.montant || 0} €`,
                montant: ex.montant || null,
                reference: ex.siren || null,
                linked_document_id: linkedDocumentId,
              }
            : {
                type: "presence_assemblees",
                date_operation: ex.date_assemblee || new Date().toISOString().slice(0, 10),
                titre: `Feuille présence ${(ex.type_assemblee || "AG").toUpperCase()} du ${ex.date_assemblee || "?"}`,
                description: `${(ex.presents || []).length} présent(s) · Parts présentes: ${ex.total_parts_presentes || 0} · Quorum: ${ex.quorum_atteint ? "oui" : "non"}`,
                reference: ex.siren || null,
                linked_document_id: linkedDocumentId,
              };
          await api.post("/legal/registers", entry);
          autoRegisterCreated = true;
        } catch { /* silent */ }
      }
      setResult({ ...r.data, __url: url, __auto_register: autoRegisterCreated, __linked_document_id: linkedDocumentId });
      if (docType === "bilan" && r.data.draft) {
        toast.success(`Brouillon AGO exercice ${r.data.exercice} pré-rempli`);
        onAgoPrefilled?.(r.data);
      } else if (docType === "cession_parts") {
        toast.success("Champs AGE « Cession de parts » extraits");
      } else if (docType === "depot_greffe") {
        toast.success(`Dépôt enregistré · ${r.data.alertes_verrouillees || 0} alerte(s) verrouillée(s)`);
      } else if (autoRegisterCreated) {
        toast.success(
          (docType === "convention_reglementee"
            ? "Convention ajoutée au registre"
            : "Feuille de présence ajoutée au registre")
          + (linkedDocumentId ? " — PDF lié au coffre-fort" : "")
        );
      } else {
        toast.success("Extraction IA terminée");
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const extracted = result?.extracted || null;
  const extractedEntries = extracted ? Object.entries(extracted).filter(([k]) => k !== "type_document") : [];

  return (
    <>
      <Card className="border-blue-200" data-testid="ocr-extract-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-600" />
            OCR IA — Extraction typée
            <Badge variant="outline" className="text-[10px] font-normal">10 types supportés</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-[280px_1fr_auto] gap-2 items-end">
            <div>
              <label className="text-xs text-slate-600 mb-1 block">Type de document</label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="ocr-doctype-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      <span className="font-medium">{t.label}</span>
                      <span className="text-slate-400 text-xs ml-2">— {t.hint}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <input
              id="ocr-file-input"
              name="ocr-file-input"
              ref={inputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={(e) => extract(e.target.files?.[0])}
              className="text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 file:font-medium file:cursor-pointer"
              data-testid="ocr-file-input"
              disabled={loading}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
              data-testid="ocr-trigger-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {loading ? "Analyse..." : "Choisir un fichier"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {DOC_TYPES.find((t) => t.key === docType)?.hint}
            {docType === "bilan" && " · le brouillon AGO est mis à jour automatiquement"}
            {docType === "depot_greffe" && " · les alertes échéances liées sont fermées"}
          </p>
        </CardContent>
      </Card>

      <Dialog open={!!result} onOpenChange={() => setResult(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Extraction — {DOC_TYPES.find((t) => t.key === docType)?.label}
            </DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
          </DialogHeader>
          {result && (
            <div className="space-y-3">
              {result.persisted && (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 p-2 rounded">
                  <CheckCircle2 className="w-4 h-4" />
                  Brouillon AGO exercice {result.exercice} enregistré.
                </div>
              )}
              {result.age_type && result.ready_to_generate && (
                <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 p-2 rounded">
                  <ArrowRight className="w-4 h-4" />
                  Prêt à générer une AGE typée « {result.age_type.replace("_", " ")} ». Ouvrez l'onglet Assemblées → Nouvelle AGE.
                </div>
              )}
              {result.alertes_verrouillees > 0 && (
                <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 p-2 rounded">
                  <CheckCircle2 className="w-4 h-4" />
                  {result.alertes_verrouillees} alerte(s) « dépôt comptes » verrouillée(s).
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Champs extraits</p>
                <div className="border rounded divide-y">
                  {extractedEntries.map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between px-3 py-1.5 text-sm hover:bg-slate-50">
                      <span className="text-slate-500 font-mono text-xs">{k}</span>
                      <span className="text-slate-800 font-medium text-right ml-4 max-w-md truncate">{formatVal(v)}</span>
                    </div>
                  ))}
                  {extractedEntries.length === 0 && <p className="p-3 text-sm text-slate-500">Aucun champ détecté</p>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
